function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
var _menuDrawerHiddenWidth = _classPrivateFieldLooseKey("menuDrawerHiddenWidth");
var _intersectionObserver = _classPrivateFieldLooseKey("intersectionObserver");
var _offscreen = _classPrivateFieldLooseKey("offscreen");
var _lastScrollTop = _classPrivateFieldLooseKey("lastScrollTop");
var _timeout = _classPrivateFieldLooseKey("timeout");
var _scrollRafId = _classPrivateFieldLooseKey("scrollRafId");
var _resizeObserver = _classPrivateFieldLooseKey("resizeObserver");
var _observeStickyPosition = _classPrivateFieldLooseKey("observeStickyPosition");
var _handleOverflowMinimum = _classPrivateFieldLooseKey("handleOverflowMinimum");
var _updateMenuVisibility = _classPrivateFieldLooseKey("updateMenuVisibility");
var _handleWindowScroll = _classPrivateFieldLooseKey("handleWindowScroll");
var _updateScrollState = _classPrivateFieldLooseKey("updateScrollState");
class HeaderComponent extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Updates the visibility of the menu and drawer
     * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
     */
    Object.defineProperty(this, _updateMenuVisibility, {
      value: _updateMenuVisibility2
    });
    this.requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];
    /**
     * Width of window when header drawer was hidden
     * @type {number | null}
     */
    Object.defineProperty(this, _menuDrawerHiddenWidth, {
      writable: true,
      value: null
    });
    /**
     * An intersection observer for monitoring sticky header position
     * @type {IntersectionObserver | null}
     */
    Object.defineProperty(this, _intersectionObserver, {
      writable: true,
      value: null
    });
    /**
     * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
     * @type {boolean}
     */
    Object.defineProperty(this, _offscreen, {
      writable: true,
      value: false
    });
    /**
     * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
     * @type {number}
     */
    Object.defineProperty(this, _lastScrollTop, {
      writable: true,
      value: 0
    });
    /**
     * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
     * @type {number | null}
     */
    Object.defineProperty(this, _timeout, {
      writable: true,
      value: null
    });
    /**
     * RAF ID for scroll handler throttling
     * @type {number | null}
     */
    Object.defineProperty(this, _scrollRafId, {
      writable: true,
      value: null
    });
    /**
     * Keeps the global `--header-height` custom property up to date,
     * which other theme components can then consume
     */
    Object.defineProperty(this, _resizeObserver, {
      writable: true,
      value: new ResizeObserver(([entry]) => {
        if (!entry || !entry.borderBoxSize[0]) return;

        // The initial height is calculated using the .offsetHeight property, which returns an integer.
        // We round to the nearest integer to avoid unnecessaary reflows.
        const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
        document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

        // Check if the menu drawer should be hidden in favor of the header menu
        if (_classPrivateFieldLooseBase(this, _menuDrawerHiddenWidth)[_menuDrawerHiddenWidth] && window.innerWidth > _classPrivateFieldLooseBase(this, _menuDrawerHiddenWidth)[_menuDrawerHiddenWidth]) {
          _classPrivateFieldLooseBase(this, _updateMenuVisibility)[_updateMenuVisibility](false);
        }
      })
    });
    /**
     * Observes the header while scrolling the viewport to track when its actively sticky
     * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
     */
    Object.defineProperty(this, _observeStickyPosition, {
      writable: true,
      value: (alwaysSticky = true) => {
        if (_classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver]) return;
        const config = {
          threshold: alwaysSticky ? 1 : 0
        };
        _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver] = new IntersectionObserver(([entry]) => {
          if (!entry) return;
          const {
            isIntersecting
          } = entry;
          if (alwaysSticky) {
            this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
            if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
          } else {
            _classPrivateFieldLooseBase(this, _offscreen)[_offscreen] = !isIntersecting || this.dataset.stickyState === 'active';
          }
        }, config);
        _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver].observe(this);
      }
    });
    /**
     * Handles the overflow minimum event from the header menu
     * @param {OverflowMinimumEvent} event
     */
    Object.defineProperty(this, _handleOverflowMinimum, {
      writable: true,
      value: event => {
        _classPrivateFieldLooseBase(this, _updateMenuVisibility)[_updateMenuVisibility](event.detail.minimumReached);
      }
    });
    Object.defineProperty(this, _handleWindowScroll, {
      writable: true,
      value: () => {
        if (_classPrivateFieldLooseBase(this, _scrollRafId)[_scrollRafId] !== null) return;
        _classPrivateFieldLooseBase(this, _scrollRafId)[_scrollRafId] = requestAnimationFrame(() => {
          _classPrivateFieldLooseBase(this, _scrollRafId)[_scrollRafId] = null;
          _classPrivateFieldLooseBase(this, _updateScrollState)[_updateScrollState]();
        });
      }
    });
    Object.defineProperty(this, _updateScrollState, {
      writable: true,
      value: () => {
        const stickyMode = this.getAttribute('sticky');
        if (!_classPrivateFieldLooseBase(this, _offscreen)[_offscreen] && stickyMode !== 'always') return;
        const scrollTop = document.scrollingElement?.scrollTop ?? 0;
        const headerTop = this.getBoundingClientRect().top;
        const isScrollingUp = scrollTop < _classPrivateFieldLooseBase(this, _lastScrollTop)[_lastScrollTop];
        const isAtTop = headerTop >= 0;
        if (_classPrivateFieldLooseBase(this, _timeout)[_timeout]) {
          clearTimeout(_classPrivateFieldLooseBase(this, _timeout)[_timeout]);
          _classPrivateFieldLooseBase(this, _timeout)[_timeout] = null;
        }
        if (stickyMode === 'always') {
          if (isAtTop) {
            this.dataset.scrollDirection = 'none';
          } else if (isScrollingUp) {
            this.dataset.scrollDirection = 'up';
          } else {
            this.dataset.scrollDirection = 'down';
          }
          _classPrivateFieldLooseBase(this, _lastScrollTop)[_lastScrollTop] = scrollTop;
          return;
        }
        if (isScrollingUp) {
          if (isAtTop) {
            // reset sticky state when header is scrolled up to natural position
            _classPrivateFieldLooseBase(this, _offscreen)[_offscreen] = false;
            this.dataset.stickyState = 'inactive';
            this.dataset.scrollDirection = 'none';
          } else {
            // show sticky header when scrolling up
            this.dataset.stickyState = 'active';
            this.dataset.scrollDirection = 'up';
          }
        } else if (this.dataset.stickyState === 'active') {
          this.dataset.scrollDirection = 'none';
          this.dataset.stickyState = 'idle';
        } else {
          this.dataset.scrollDirection = 'none';
          this.dataset.stickyState = 'idle';
        }
        _classPrivateFieldLooseBase(this, _lastScrollTop)[_lastScrollTop] = scrollTop;
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    _classPrivateFieldLooseBase(this, _resizeObserver)[_resizeObserver].observe(this);
    this.addEventListener('overflowMinimum', _classPrivateFieldLooseBase(this, _handleOverflowMinimum)[_handleOverflowMinimum]);
    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      _classPrivateFieldLooseBase(this, _observeStickyPosition)[_observeStickyPosition](stickyMode === 'always');
      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', _classPrivateFieldLooseBase(this, _handleWindowScroll)[_handleWindowScroll]);
      }
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    _classPrivateFieldLooseBase(this, _resizeObserver)[_resizeObserver].disconnect();
    _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver]?.disconnect();
    this.removeEventListener('overflowMinimum', _classPrivateFieldLooseBase(this, _handleOverflowMinimum)[_handleOverflowMinimum]);
    document.removeEventListener('scroll', _classPrivateFieldLooseBase(this, _handleWindowScroll)[_handleWindowScroll]);
    if (_classPrivateFieldLooseBase(this, _scrollRafId)[_scrollRafId] !== null) {
      cancelAnimationFrame(_classPrivateFieldLooseBase(this, _scrollRafId)[_scrollRafId]);
      _classPrivateFieldLooseBase(this, _scrollRafId)[_scrollRafId] = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}
function _updateMenuVisibility2(hideMenu) {
  if (hideMenu) {
    _classPrivateFieldLooseBase(this, _menuDrawerHiddenWidth)[_menuDrawerHiddenWidth] = window.innerWidth;
    this.dataset.menuDrawerWidth = String(window.innerWidth);
  } else {
    _classPrivateFieldLooseBase(this, _menuDrawerHiddenWidth)[_menuDrawerHiddenWidth] = null;
    delete this.dataset.menuDrawerWidth;
  }
  setHeaderMenuStyle();
}
if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(entries => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (entry.target !== header || header.hasAttribute('data-transparent') && header.parentElement?.nextElementSibling) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });
    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });
    mutationObserver.observe(headerGroup, {
      childList: true
    });
  }
});
